import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";

import {
    AiGeneratedSlideGroupType,
    AiGeneratedSlideTypeGroups,
    ContextSource,
    ContextSourceType,
    GeneratedSlideModel
} from "common/aiConstants";
import { IPresentation, MetricName, WorkspaceResourceType } from "common/interfaces";
import { FlexLayout } from "js/Components/FlexLayout";
import { Model as BackboneModel } from "backbone";
import { isPPTAddin } from "js/config";
import getLogger, { LogGroup } from "js/core/logger";
import AiGenerationService, {
    GenerateSlideRequestWithFiles,
    ContextFileTooLargeError
} from "js/core/services/aiGeneration";
import * as ApiErrors from "apis/errors";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { ShowInsufficientCreditsDialog, ShowTooManyRequestsDialog } from "js/react/components/Dialogs/CreditsErrorDialog";
import Spinner from "js/react/components/Spinner";
import { Presentation } from "js/core/models/presentation";
import { SharedTheme } from "js/core/models/sharedTheme";
import { app } from "js/namespaces";
import AppController from "js/core/AppController";
import WorkspaceController from "js/controllers/WorkspaceController";
import { t } from "js/i18n";

import AdditionalContextInput from "../../components/AdditionalContextInput";
import {
    PaneContainer,
    PaneContent,
    PaneFooter,
    PromptSectionContainer
} from "../../components/PaneComponents";
import PromptInput from "../../components/PromptInput";

const logger = getLogger(LogGroup.AI);

const promptExamples = [
    "A quote by Albert Einstein about the universe",
    "Chart the population of the world between 1900 and now",
    "Compare popular vacuum cleaners",
    "Top 3 women entrepreneurs",
    "Google's ad revenue in 2022",
    "How to write a blog post in a few easy steps",
    "Why did the chicken cross the road?",
    "Logos of the top 9 consumer companies",
    "Wordcloud about business metrics",
    "Pie chart of the market share of popular social media networks",
    "Top 5 movies of 2010 with movie posters and box office revenue",
    "Timeline of great future inventions",
    "Create a table for the last 10 years of the Superbowl including the year, the winning team, the losing team, the winning quarterback and the score",
];

const GenerationProgress = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .spinner {
        position: relative !important;
        width: 75px;
        height: 75px;
    }

    > label {
        font-family: "Source Sans Pro Heavy";
        font-size: 20px;
        font-weight: 900;
        max-width: 60%;
        text-align: center;
    }
`;

interface GenerateSlidePaneProps {
    onGeneratedSlide: (model: GeneratedSlideModel, prompt: string, generateSlideRequest: GenerateSlideRequestWithFiles) => void;
    forTeamSlide?: boolean;
    presentation: BackboneModel<IPresentation & { isDummy: boolean, link: { id: string } }> | null;
    currentSlide: any | null;
    currentTheme: any | null;
}

function GenerateSlidePane({ onGeneratedSlide, forTeamSlide = false, presentation, currentSlide, currentTheme }: GenerateSlidePaneProps) {
    const [generationMessage, setGenerationMessage] = useState<string>("");
    const [slideGroupType, setSlideGroupType] = useState<AiGeneratedSlideGroupType>(AiGeneratedSlideGroupType.ALL_BUT_TITLE_AND_CONCLUSION);
    const [contextSource, setContextSource] = useState<ContextSource | null>(null);
    const [usePresentationAsContext, setUsePresentationAsContext] = useState<boolean>(!isPPTAddin);
    const [language, setLanguage] = useState<string>("");
    const [prompt, setPrompt] = useState<string>("");

    const createDummyPresentation = useCallback(async () => {
        try {
            const resources = WorkspaceController.resources;
            const themeId = resources.find(r => r.resourceType === WorkspaceResourceType.THEME && r.metadata.isDefault)?.resourceId;

            if (!themeId) {
                ShowErrorDialog({ title: "Team Theme Not Found", message: "The team theme could not be found." });
                return;
            }

            const teamTheme = new SharedTheme({ id: themeId }, { autoLoad: false });
            await teamTheme.load();
            await teamTheme.loadTheme();

            const dummyPresentation = new Presentation({
                name: "Team Slide",
                userId: app.user.id,
                orgId: AppController.orgId,
                themeId: teamTheme.id,
                sharedThemeId: teamTheme.id,
                isDummy: true
            }, { disconnected: true, userId: app.user.id });

            await dummyPresentation.load();
            dummyPresentation.permissions.write = true;
            dummyPresentation.permissions.read = true;

            await PresentationEditorController.setPresentation(dummyPresentation);
        } catch (err) {
            logger.error(err, "[GenerateSlidePane] Failed to create dummy presentation");
            ShowErrorDialog({
                title: "Error",
                message: "Failed to initialize presentation for slide generation."
            });
        }
    }, []);

    useEffect(() => {
        // If we're creating a shared slide/template and there's no presentation, create a dummy one
        if (forTeamSlide && !presentation) {
            createDummyPresentation();
        }
    }, [forTeamSlide, presentation]);

    // Sync currentSlide, currentTheme, and language with presentation
    useEffect(() => {
        if (presentation) {
            // in case a dummy presentation also has a language, use it
            setLanguage(presentation.get("metadata")?.language ?? "");
        }
    }, [presentation]);

    const handleStartGeneration = async (newPrompt: string) => {
        const startedAt = Date.now();
        let withContext = false;
        let imageCount = 0;
        let templateId = null;

        try {
            setGenerationMessage("DesignerBot AI is creating a slide...");

            const generateSlideRequest: GenerateSlideRequestWithFiles = {
                prompt: newPrompt ?? prompt,
                // Don't use presentation as context if it's a dummy presentation (not saved in database)
                ...(!isPPTAddin && presentation && presentation?.get("isDummy") !== true ? {
                    usePresentationAsContext,
                    presentationId: presentation.id as string,
                    newSlideIndex: currentSlide?.getIndex() ?? 1,
                } : {}),
                allowedSlideTypes: AiGeneratedSlideTypeGroups[slideGroupType],
                files: [],
                contextUrls: [],
                language
            };

            if (contextSource) {
                switch (contextSource.type) {
                    case ContextSourceType.FILE:
                        if (contextSource.file) generateSlideRequest.files.push(contextSource.file);
                        break;
                    case ContextSourceType.WEBPAGE:
                        if (contextSource.url) generateSlideRequest.contextUrls.push(contextSource.url);
                        break;
                    case ContextSourceType.TEXT:
                        if (contextSource.text) generateSlideRequest.files.push(new File([contextSource.text], "document.txt", { type: "text/plain" }));
                        break;
                }
            }

            withContext = generateSlideRequest.contextUrls?.length > 0 || generateSlideRequest.files?.length > 0;

            if (presentation && presentation.get("isDummy") !== true) {
                const presentationImageStyle = presentation.get("metadata")?.imageStyle;
                const presentationImageStylePrompt = presentation.get("metadata")?.imageStylePrompt;
                const themeImageStylePrompt = currentTheme?.get("imageStylePrompt");

                generateSlideRequest.imageStyle = presentationImageStyle;
                generateSlideRequest.imageStylePrompt = presentationImageStyle === "stock" ? "" : presentationImageStyle === "theme" ? themeImageStylePrompt : presentationImageStylePrompt;
            }

            const model = await AiGenerationService.generateSlide(generateSlideRequest);

            templateId = model.template_id;

            onGeneratedSlide(
                model,
                prompt,
                generateSlideRequest
            );

            logger.metric(MetricName.AI_GENERATE_SLIDE_TIME, {
                generationTimeMs: Date.now() - startedAt,
                generationFailed: false,
                withContext,
                imageCount,
                templateId
            });
        } catch (err) {
            logger.error(err, "[GenerateSlidePane] handleGenerateSlide() failed", { prompt });

            if (err instanceof ApiErrors.PaymentRequiredError) {
                ShowInsufficientCreditsDialog();
            } else if (err instanceof ApiErrors.TooManyRequestsError) {
                ShowTooManyRequestsDialog();
            } else if (err instanceof ApiErrors.UnprocessableContentError) {
                ShowErrorDialog({
                    title: t("Sorry, we do not support the file type you have provided."),
                    message: t("Please try again with a text file, word document, or PDF.")
                });
            } else if (err instanceof ApiErrors.ContentTooLargeError || err instanceof ContextFileTooLargeError) {
                ShowErrorDialog({
                    title: t("Sorry, the context you have provided is too large."),
                    message: err.message
                });
            } else {
                ShowErrorDialog({
                    title: t("Sorry, something went wrong while generating your slide!"),
                    message: t("Sometimes DesignerBot's AI can get confused. You can try your prompt again or edit it.")
                });
            }

            setGenerationMessage("");

            logger.metric(MetricName.AI_GENERATE_SLIDE_TIME, {
                generationTimeMs: Date.now() - startedAt,
                generationFailed: true,
                withContext,
                imageCount,
                templateId
            });
        }
    };

    const handleEnterKeyPress = (newPrompt: string) => {
        if (newPrompt.trim().length > 0) {
            handleStartGeneration(newPrompt);
        }
    };

    return (<PaneContainer cta="SlideBot">
        {generationMessage &&
            <GenerationProgress>
                <Spinner />
                <label>{generationMessage}</label>
            </GenerationProgress>
        }
        {!generationMessage &&
            <>
                <PaneContent>
                    <FlexLayout direction="column" width="fill" height="fill" gap={20} scrollable>
                        <PromptSectionContainer>
                            <PromptInput
                                label="Generate a slide with AI"
                                prompt={prompt}
                                onPromptChange={setPrompt}
                                promptPlaceholder="Describe the topic of your slide..."
                                promptExamples={promptExamples}
                                onEnterKeyPress={handleEnterKeyPress}
                                multiline={true}
                            />
                        </PromptSectionContainer>

                        <PromptSectionContainer>
                            <AdditionalContextInput
                                contextSource={contextSource}
                                onContextSourceChange={setContextSource}
                                language={language}
                                onLanguageChange={setLanguage}
                                usePresentationAsContext={usePresentationAsContext}
                                onUsePresentationAsContextChange={isPPTAddin || presentation?.get("isDummy") === true ? undefined : setUsePresentationAsContext}
                                slideGroupType={slideGroupType}
                                onSlideGroupTypeChange={setSlideGroupType}
                                allowedSlideGroupTypes={Object.values(AiGeneratedSlideGroupType).filter(type => type !== AiGeneratedSlideGroupType.PRESENTATION)}
                            />
                        </PromptSectionContainer>
                    </FlexLayout>
                </PaneContent>
                <PaneFooter
                    actionButtonText="Generate Slide"
                    onActionButtonClick={() => handleStartGeneration(prompt)}
                    actionButtonDisabled={prompt.trim().length === 0 && !contextSource}
                />
            </>
        }
    </PaneContainer >);
}

export default PresentationEditorController.withState(GenerateSlidePane);
